import { AmlHistoryItem, AmlHistoryResponse } from '@/api';
import { getTxAmount, getAssetPrecisions, getRiskScoreValue } from '@/helpers';
import { AssetPrecisionEnum, CheckItemTypeEnum } from '@/config';
import { AmlHistoryData, AmlHistoryItemData } from '../models';

const FIAT_CODE_DEFAULT = 'usd';

const txAmountData = (item: AmlHistoryItem) => {
  let riskyValue = 0;

  if (item.risky_volume_usd && (item.risky_volume_usd || 0) !== 0) {
    riskyValue = Number(item.risky_volume_usd) / 100;
  } else if (item.risky_volume_fiat && Number(item.risky_volume_fiat) > 0) {
    riskyValue = Number(item.risky_volume_fiat) / 100;
  }

  const fiatCode =
    riskyValue > 0
      ? (item.fiat_code_effective || FIAT_CODE_DEFAULT).toUpperCase()
      : '';
  const riskyUsd = riskyValue > 0 ? `${riskyValue.toFixed(2)} ${fiatCode}` : '';
  const hasRiskyAmount = !!Math.round(item.risky_volume || 0);

  if (item.tokenDetails) {
    const token = item.tokenDetails;
    const viewPrecision = AssetPrecisionEnum.TOKEN_VIEW_PRECISION_DEFAULT;

    return {
      viewPrecision,
      precision: token.precision,
      asset: token.code,
      amount: getTxAmount(item.amount || 0, token.precision, viewPrecision),
      riskyAmount: getTxAmount(
        Math.round(item.risky_volume || 0),
        token.precision,
        viewPrecision,
      ),
      riskyUsd,
      hasRiskyAmount,
    };
  }

  const [precision, viewPrecision] = getAssetPrecisions(
    item.asset,
    item.network,
  );

  return {
    viewPrecision,
    precision,
    asset: item.asset,
    amount: getTxAmount(item.amount || 0, precision, viewPrecision),
    riskyAmount: getTxAmount(
      Math.round(item.risky_volume || 0),
      precision,
      viewPrecision,
    ),
    riskyUsd,
    hasRiskyAmount,
  };
};

const mapAmlHistoryItem = (item: AmlHistoryItem) => {
  const [precision, viewPrecision] = getAssetPrecisions(
    item.asset,
    item.network,
  );

  return <AmlHistoryItemData>{
    uuid: item.uid,
    address: item.address,
    // not gated on `item.tx`: an approximate tx check reports the submitted hash
    // here while `tx` is still empty
    originalTx: item.originalTx,
    addressOwner: item.counterparty?.name || item.counterparty?.slug,
    riskscore: getRiskScoreValue(item.riskscore),
    riskScoreLevel: item.risk_score_level,
    reportedAddressBalance: item.reportedAddressBalance || '',
    blackListsConnections: item.blackListsConnections,
    hasBlackListFlag: item.hasBlackListFlag || false,
    pdfReport: item.pdfReport || '',
    memo: item.memo || '',
    asset: item.asset,
    network: item.network,
    flow: item.flow,
    status: item.status,
    updated_at: item.updated_at * 1000,
    timestamp: +new Date(item.timestamp),
    signals: item.signals,
    signalTags: item.signalTags,
    addressDetailsData: item.addressDetailsData,
    counterparty: item.counterparty,
    type: item._type,
    extras: item.extras,
    cost: item.cost,
    customerIsB2B: item.customerIsB2B,
    hasHighRisk: item?.hasHighRisk || false,
    devMode: item?.devMode || false,
    itemKindShort: !!item.tx ? CheckItemTypeEnum.TX : CheckItemTypeEnum.ADDRESS,
    ...(!!item.tx
      ? {
          tx: item.tx,
          tokenDetails: item.tokenDetails,
          direction: item.direction,
          amount: item.amount
            ? getTxAmount(item.amount, precision, viewPrecision)
            : null,
          risky_volume: item.risky_volume,
          risky_volume_fiat: item.risky_volume_fiat,
          fiat: item.fiat,
          fiat_code_effective: item.fiat_code_effective,
          risky_volume_usd: item.risky_volume_usd
            ? parseFloat(item.risky_volume_usd.toString())
            : 0,
          amountData: txAmountData(item),
          connections: item.connections,
        }
      : {}),
  };
};

const amlHistory = (response: AmlHistoryResponse): AmlHistoryData => {
  const { data } = response;
  const list = data.data as AmlHistoryItem[];

  const mappedData: AmlHistoryData = {
    result: data.result,
    pageLimit: data.pageLimit,
    totalCount: data.totalCount,
    balance: data.balance,
    amlFlow: data.amlFlow,
    discount: data.discount,
    discountDueTime: data.discountDueTime,
    promoFlow: data.promoFlow,
    usePromoFlow: data.usePromoFlow,
    devModeUsed: !!data?.devModeUsed,
    items: list.map(mapAmlHistoryItem),
  };

  return mappedData;
};

export { mapAmlHistoryItem, amlHistory };
